import {
  ButtonBase,
  Collapse as MuiCollapse,
  Divider as MuiDivider,
  Typography,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { Inline, Stack, useResponsiveValue } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import React from 'react';
import { trackEvent } from 'shared/helpers/Analytics';
import {
  canHaveSimilarSearch,
  createSearchCriteria,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import styled from 'styled-components';
import { PostingDTO } from '../../data/dto/PostingDTO';
import { usePostingsPageContext } from '../postings/PostingsPageContext';
import { QuickLoadAlertButton } from '../postings/QuickLoadAlertButton';
import { CreateLoadAlertSection } from './CreateLoadAlertSection';
import {
  useSimilarLoads,
  useSimilarLoadsVisibility,
} from './SimilarLoadsHelpers';

const Container = styled.div`
  padding-top: 64px;
  padding-bottom: 32px;
`;

const Collapse = styled(MuiCollapse)`
  width: 100%;
`;

const SimilarLoadItem = styled.div`
  width: 100%;
`;

const Divider = styled(MuiDivider)`
  width: 100px;
`;

interface SimilarLoadsProps {
  existingPostings: PostingDTO[];
  renderLoadCard: (posting: PostingDTO, position: number) => React.ReactNode;
}

export function SimilarLoads({
  existingPostings,
  renderLoadCard,
}: SimilarLoadsProps) {
  const { params } = usePostingsPageContext();

  if (!canHaveSimilarSearch(params.search_criteria)) {
    return null;
  }

  return (
    <SimilarLoadsContent
      existingPostings={existingPostings}
      renderLoadCard={renderLoadCard}
    />
  );
}

function SimilarLoadsContent({
  renderLoadCard,
  existingPostings,
}: SimilarLoadsProps) {
  const { params, setParams } = usePostingsPageContext();
  const isMobile = useResponsiveValue(true, true, false);
  const [isHidden, setHidden] = useSimilarLoadsVisibility(existingPostings);

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSimilarLoads(existingPostings);

  if (!data) {
    return null;
  }

  if (!data.length) {
    return (
      <Container>
        <Stack align="center" space="medium">
          <Typography variant="h4">Having trouble finding a load?</Typography>

          <Typography align="center" color="textSecondary">
            Create a load alert and be notified when a load is posted in this
            area.
          </Typography>

          <QuickLoadAlertButton
            source="Similar Loads"
            title="Create Load Alert"
            variant="neutral"
            size="medium"
            fullWidth={isMobile}
          />
          <Inline verticalAlign="center" space="small">
            <Divider />
            <Typography color="textSecondary">or</Typography>
            <Divider />
          </Inline>
          <Stack space="small" align="center">
            <Typography color="textSecondary">
              Try removing filters to see more results.
            </Typography>
            <Button
              onClick={() => {
                setParams((prev) => ({
                  ...prev,
                  current_saved_search_guid: undefined,
                  search_criteria: createSearchCriteria({}),
                }));
              }}
            >
              Clear all filters
            </Button>
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack space="small" align="center">
        <Stack space="xxsmall">
          <Typography variant="h3" align="center">
            Similar Loads Available
          </Typography>

          <Typography align="center" color="textSecondary">
            Along Route and/or in Larger Radius
          </Typography>
        </Stack>

        <Collapse in={!isHidden} appear={true} enter={true}>
          <Stack space="small" align="center">
            {data.map((posting, index) => (
              <SimilarLoadItem key={posting.load.guid}>
                {renderLoadCard(posting, index)}
              </SimilarLoadItem>
            ))}

            {hasNextPage && (
              <Button
                variant="neutral"
                pending={isFetchingNextPage}
                onClick={() => {
                  fetchNextPage();
                  trackEvent('SLB: Clicked Show More Similar Loads', {
                    results_count: data.length,
                  });
                }}
              >
                Show More Similar Loads
              </Button>
            )}
          </Stack>
        </Collapse>

        {isHidden ? (
          <Button
            fullWidth={isMobile}
            endIcon={<KeyboardArrowDown />}
            onClick={() => {
              setHidden(false);
              trackEvent('SLB: Clicked Show Similar Loads', {
                results_count: data.length,
              });
            }}
          >
            Show Similar Loads
          </Button>
        ) : (
          <ButtonBase
            onClick={() => {
              setHidden(true);
              trackEvent('SLB: Clicked Hide Similar Loads', {
                results_count: data.length,
              });
            }}
          >
            Hide Similar Loads <KeyboardArrowUp color="action" />
          </ButtonBase>
        )}

        {!params.current_saved_search_guid && <CreateLoadAlertSection />}
      </Stack>
    </Container>
  );
}
